import React, { useState, useEffect, useRef } from 'react';
import { classificationService } from '../../services/classificationService';
import { productService } from '../../services/productService';
import { getModuleSettings } from '../../services/moduleSettingsService';
import { useModuleSettings } from '../../contexts/ModuleSettingsContext';
import ClassificationSelector from './ClassificationSelector';
import ProductConversionsSection from './ProductConversionsSection';
import Toast from '../Common/Toast';
import { useCanMutate } from '../../hooks/useCanMutate';

const ProductForm = ({ isOpen, onClose, product = null, onSuccess, isPageMode = false, recipeLinkedMode = false }) => {
  const readOnly = !useCanMutate();
  const { machineryEnabled, produccionEnabled, produccionConcesionariaEnabled, restaurantesEnabled, ventasEnabled } =
    useModuleSettings();
  /** Insumo: visible si Producción, Producción concesionaria o Restaurantes están activos. */
  const insumoModulesEnabled =
    produccionEnabled || produccionConcesionariaEnabled || restaurantesEnabled;
  const lockNonVendibleFields = Boolean(recipeLinkedMode);
  const [formData, setFormData] = useState({
    family_id: '',
    name: '',
    unit_of_measure: 'UN',
    min_stock: 0,
    is_active: true,
    is_trackable: true,
    is_serializable: false,
    has_price_list: false, // Toggle para mostrar/ocultar sección de precios
    has_pos_prices: false,
    is_taxable: false,
    pos_prices: {}, // { point_of_sale_id: price }
    brand: '',
    model: '',
    weight: '',
    length: '',
    width: '',
    height: '',
    color: '',
    size: '',
    warehouse_location: '',
    attributes: {}, // { attribute_id: value }
    machinery_register_enabled: false, // Habilitar registro de lectura de vehículo/máquina (módulo Maquinaria)
    is_insumo: false, // Producción concesionaria / Restaurantes: insumo
    is_vendible: false, // Ventas: producto vendible en lista de precios
    warehouse_ids: [],
    conversions: [],
  });

  const [hierarchy, setHierarchy] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [families, setFamilies] = useState([]);
  const [units, setUnits] = useState([]);
  const [attributes, setAttributes] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [pointOfSales, setPointOfSales] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [loadingSubcategories, setLoadingSubcategories] = useState(false);
  const [loadingFamilies, setLoadingFamilies] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [skuPreview, setSkuPreview] = useState('');
  const [toast, setToast] = useState({ isOpen: false, message: '', type: 'info' });
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [warehouseSelectionEnabled, setWarehouseSelectionEnabled] = useState(false);
  const [productConversionsEnabled, setProductConversionsEnabled] = useState(false);
  const hasLoadedFormData = useRef(false);
  const isLoadingFormDataRef = useRef(false);
  const hasLoadedProduct = useRef(false);

  // Cargar todos los datos necesarios para el formulario (solo una vez)
  useEffect(() => {
    if ((isOpen || isPageMode) && !hasLoadedFormData.current) {
      hasLoadedFormData.current = true;
      console.log('🔄 [ProductForm] Primera carga - Iniciando carga de todos los datos del formulario...');
      loadProductFormData();
    } else if (isOpen || isPageMode) {
      console.log('⏭️ [ProductForm] Ya se cargaron los datos anteriormente, omitiendo carga duplicada');
    }
    
    // Resetear el flag cuando se cierra el modal (solo si no es modo página)
    return () => {
      if (!isPageMode && !isOpen) {
        hasLoadedFormData.current = false;
        console.log('🔄 [ProductForm] Modal cerrado, reseteando flag de carga');
      }
    };
  }, [isOpen, isPageMode]);

  useEffect(() => {
    let cancelled = false;
    getModuleSettings()
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) {
          setWarehouseSelectionEnabled(Boolean(res.data.product_warehouse_visibility_enabled));
          setProductConversionsEnabled(Boolean(res.data.product_conversions_enabled));
        }
      })
      .catch(() => {
        if (!cancelled) setWarehouseSelectionEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (formData.family_id && formData.category_id && formData.subcategory_id && hierarchy.length > 0) {
      generateSkuPreview();
    } else if (!formData.family_id) {
      setSkuPreview('');
    }
  }, [formData.family_id, formData.category_id, formData.subcategory_id, hierarchy]);

  // Función para cargar datos del producto desde el backend
  const loadProductData = async (productId) => {
    if (!productId || loadingProduct) return;
    
    setLoadingProduct(true);
    console.log('📡 [ProductForm] Cargando producto desde backend, ID:', productId);
    
    try {
      const response = await productService.getProduct(productId);
      
      if (response.success && response.data) {
        console.log('✅ [ProductForm] Producto cargado exitosamente:', response.data);
        loadProductDataFromObject(response.data);
      } else {
        console.warn('⚠️ [ProductForm] Respuesta sin éxito:', response);
        setToast({
          isOpen: true,
          message: 'Error al cargar el producto',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('❌ [ProductForm] Error al cargar producto:', error);
      setToast({
        isOpen: true,
        message: error.response?.data?.message || 'Error al cargar el producto',
        type: 'error'
      });
    } finally {
      setLoadingProduct(false);
    }
  };

  // Función para cargar datos del producto desde un objeto
  const loadProductDataFromObject = (product) => {
    console.log('📝 [ProductForm] Cargando datos del producto desde objeto:');
    console.log('📦 [ProductForm] Product data:', product);
    
    const familyId = product.family_id || product.family?.id || '';
    const family = product.family;
    const subcategoryId = family?.subcategory_id || family?.subcategory?.id || '';
    const categoryId = family?.subcategory?.category_id || family?.subcategory?.category?.id || '';
    
    console.log('🔗 [ProductForm] Datos de relaciones del producto:');
    console.log('   - Family ID:', familyId);
    console.log('   - Subcategory ID:', subcategoryId);
    console.log('   - Category ID:', categoryId);
    
    // Procesar atributos - convertir los IDs de string a número si es necesario
    const processedAttributes = {};
    if (product.attributes && typeof product.attributes === 'object') {
      Object.keys(product.attributes).forEach(key => {
        const attrId = parseInt(key);
        if (!isNaN(attrId) && attrId > 0) {
          processedAttributes[attrId] = product.attributes[key];
        }
      });
    }
    
    // Procesar precios por punto de venta - convertir los IDs de string a número si es necesario
    const processedPosPrices = {};
    if (product.pos_prices && typeof product.pos_prices === 'object') {
      Object.keys(product.pos_prices).forEach(key => {
        const posId = parseInt(key);
        if (!isNaN(posId) && posId > 0) {
          processedPosPrices[posId] = product.pos_prices[key];
        }
      });
    }
    
    console.log('📝 [ProductForm] Procesando datos del producto:');
    console.log('   - Attributes originales:', product.attributes);
    console.log('   - Attributes procesados:', processedAttributes);
    console.log('   - Pos Prices originales:', product.pos_prices);
    console.log('   - Pos Prices procesados:', processedPosPrices);
    
    setFormData({
      category_id: categoryId || '',
      subcategory_id: subcategoryId || '',
      family_id: familyId,
      name: product.name || '',
      unit_of_measure: product.unit_of_measure || 'UN',
      min_stock: product.min_stock || 0,
      is_active: product.is_active !== undefined ? product.is_active : true,
      has_price_list: product.has_price_list !== undefined ? product.has_price_list : false,
      has_pos_prices: product.has_price_list !== undefined ? product.has_price_list : false,
      is_taxable: product.is_taxable !== undefined ? product.is_taxable : false,
      pos_prices: processedPosPrices,
      weight: product.weight ? String(product.weight) : '',
      length: product.length ? String(product.length) : '',
      width: product.width ? String(product.width) : '',
      height: product.height ? String(product.height) : '',
      attributes: processedAttributes,
      machinery_register_enabled: product.machinery_register_enabled === true,
      is_insumo: product.is_insumo === true,
      is_vendible: product.is_vendible === true,
      warehouse_ids: Array.isArray(product.warehouse_ids) ? product.warehouse_ids.map((id) => Number(id)) : [],
      conversions: Array.isArray(product.conversions)
        ? product.conversions.map((row, index) => ({
            key: row.id ? `existing-${row.id}` : `loaded-${index}`,
            id: row.id,
            from_unit_id: row.from_unit_id || '',
            to_unit_id: row.to_unit_id || '',
            conversion_rate: row.conversion_rate != null ? String(row.conversion_rate) : '',
            description: row.description || '',
            is_active: row.is_active !== false,
          }))
        : [],
    });
    
    if (product.sku) {
      setSkuPreview(product.sku);
    }
  };

  // Cargar producto si se pasa un ID (modo edición)
  useEffect(() => {
    if (product && typeof product === 'object' && product.id && !hasLoadedProduct.current) {
      hasLoadedProduct.current = true;
      loadProductData(product.id);
    } else if (product && typeof product === 'object' && !product.id) {
      // Si product es un objeto pero no tiene ID, usar los datos directamente
      loadProductDataFromObject(product);
    } else if (!product) {
      // Modo creación
      setFormData({
        category_id: '',
        subcategory_id: '',
        family_id: '',
        name: '',
        unit_of_measure: 'UN',
        min_stock: 0,
        is_active: true,
        is_trackable: true,
        is_serializable: false,
        has_price_list: false,
        has_pos_prices: false,
        is_taxable: false,
        pos_prices: {},
        brand: '',
        model: '',
        weight: '',
        length: '',
        width: '',
        height: '',
        color: '',
        size: '',
warehouse_location: '',
    attributes: {},
    machinery_register_enabled: false,
    is_insumo: false,
    is_vendible: false,
    warehouse_ids: [],
    conversions: [],
  });
  setSkuPreview('');
  }
  setErrors({});
  }, [product, isOpen]);

  // Cargar todos los datos necesarios para el formulario en una sola consulta
  const loadProductFormData = async () => {
    // Prevenir llamadas duplicadas simultáneas
    if (isLoadingFormDataRef.current) {
      console.log('⏸️ [ProductForm] Ya hay una carga de datos en progreso, ignorando llamada duplicada');
      return;
    }

    isLoadingFormDataRef.current = true;
    setLoadingCategories(true);
    try {
      console.log('🔄 [ProductForm] Cargando todos los datos del formulario desde API...');
      const response = await classificationService.getProductFormData({ active_only: false });
      
      console.log('📥 [ProductForm] Respuesta completa del formulario:');
      console.log('   - Success:', response.success);
      console.log('   - Data type:', typeof response.data);
      console.log('   - Data keys:', response.data ? Object.keys(response.data) : 'N/A');
      
      if (response.success && response.data) {
        const { hierarchy, units, attributes, warehouses, point_of_sales } = response.data;
        
        // Procesar jerarquía (categorías, subcategorías, familias)
        console.log('📊 [ProductForm] Procesando jerarquía...');
        console.log('   - Hierarchy type:', typeof hierarchy);
        console.log('   - Hierarchy is array:', Array.isArray(hierarchy));
        console.log('   - Hierarchy length:', Array.isArray(hierarchy) ? hierarchy.length : 'N/A');
        
        if (Array.isArray(hierarchy)) {
          // En formularios de productos ocultar clasificación espejada desde recetas.
          const categoriesData = hierarchy
            .filter((category) => category?.receta_category_id == null)
            .map((category) => ({
              ...category,
              subcategories: (category.subcategories || [])
                .filter((subcategory) => subcategory?.receta_subcategory_id == null)
                .map((subcategory) => ({
                  ...subcategory,
                  families: (subcategory.families || []).filter((family) => family?.receta_family_id == null),
                })),
            }));
          console.log('✅ [ProductForm] Categorías recibidas:', categoriesData.length);
          if (categoriesData.length > 0) {
            console.log('📋 [ProductForm] Primeras 3 categorías:', categoriesData.slice(0, 3).map(c => ({
              id: c.id,
              code: c.code,
              name: c.name,
              subcategories_count: c.subcategories?.length || 0,
              is_active: c.is_active
            })));
            
            // Extraer todas las subcategorías y familias de la jerarquía
            const allSubcategories = [];
            const allFamilies = [];
            
            categoriesData.forEach(category => {
              if (category.subcategories && Array.isArray(category.subcategories)) {
                category.subcategories.forEach(subcategory => {
                  allSubcategories.push({
                    ...subcategory,
                    category: category // Incluir referencia a la categoría
                  });
                  
                  if (subcategory.families && Array.isArray(subcategory.families)) {
                    subcategory.families.forEach(family => {
                      allFamilies.push({
                        ...family,
                        subcategory: subcategory, // Incluir referencia a la subcategoría
                        category: category // Incluir referencia a la categoría
                      });
                    });
                  }
                });
              }
            });
            
            console.log('📊 [ProductForm] Subcategorías extraídas:', allSubcategories.length);
            console.log('📊 [ProductForm] Familias extraídas:', allFamilies.length);
            
            setCategories(categoriesData);
            setSubcategories(allSubcategories);
            setFamilies(allFamilies);
            setHierarchy(categoriesData); // Guardar jerarquía completa para el selector
          }
        }
        
        // Procesar unidades de medida
        console.log('📊 [ProductForm] Procesando unidades de medida...');
        console.log('   - Units type:', typeof units);
        console.log('   - Units is array:', Array.isArray(units));
        console.log('   - Units length:', Array.isArray(units) ? units.length : 'N/A');
        
        if (Array.isArray(units)) {
          console.log('✅ [ProductForm] Unidades recibidas:', units.length);
          if (units.length > 0) {
            console.log('📋 [ProductForm] Primeras 3 unidades:', units.slice(0, 3).map(u => ({
              id: u.id,
              code: u.code,
              name: u.name,
              symbol: u.symbol,
              is_active: u.is_active
            })));
          }
          setUnits(units);
        }
        
        // Procesar atributos
        console.log('📊 [ProductForm] Procesando atributos...');
        console.log('   - Attributes type:', typeof attributes);
        console.log('   - Attributes is array:', Array.isArray(attributes));
        console.log('   - Attributes length:', Array.isArray(attributes) ? attributes.length : 'N/A');
        
        if (Array.isArray(attributes)) {
          console.log('✅ [ProductForm] Atributos recibidos:', attributes.length);
          if (attributes.length > 0) {
            console.log('📋 [ProductForm] Primeros 3 atributos:', attributes.slice(0, 3).map(a => ({
              id: a.id,
              code: a.code,
              name: a.name,
              type: a.type,
              is_required: a.is_required,
              is_active: a.is_active
            })));
          }
          setAttributes(attributes);
        }
        
        // Procesar almacenes
        console.log('📊 [ProductForm] Procesando almacenes...');
        console.log('   - Warehouses type:', typeof warehouses);
        console.log('   - Warehouses is array:', Array.isArray(warehouses));
        console.log('   - Warehouses length:', Array.isArray(warehouses) ? warehouses.length : 'N/A');
        
        if (Array.isArray(warehouses)) {
          console.log('✅ [ProductForm] Almacenes recibidos:', warehouses.length);
          if (warehouses.length > 0) {
            console.log('📋 [ProductForm] Primeros 3 almacenes:', warehouses.slice(0, 3).map(w => ({
              id: w.id,
              code: w.code,
              name: w.name,
              is_active: w.is_active
            })));
          }
          setWarehouses(warehouses);
        }
        
        // Procesar puntos de venta
        console.log('📊 [ProductForm] Procesando puntos de venta...');
        console.log('   - Point of Sales type:', typeof point_of_sales);
        console.log('   - Point of Sales is array:', Array.isArray(point_of_sales));
        console.log('   - Point of Sales length:', Array.isArray(point_of_sales) ? point_of_sales.length : 'N/A');
        
        if (Array.isArray(point_of_sales)) {
          console.log('✅ [ProductForm] Puntos de venta recibidos:', point_of_sales.length);
          if (point_of_sales.length > 0) {
            console.log('📋 [ProductForm] Primeros 3 puntos de venta:', point_of_sales.slice(0, 3).map(pos => ({
              id: pos.id,
              code: pos.code,
              name: pos.name,
              location: pos.location,
              is_active: pos.is_active
            })));
          }
          setPointOfSales(point_of_sales);
        }
        
        console.log('✅ [ProductForm] Todos los datos del formulario cargados exitosamente');
      } else {
        console.warn('⚠️ [ProductForm] Respuesta sin éxito o sin data:', response);
      }
    } catch (error) {
      console.error('❌ [ProductForm] Error al cargar datos del formulario:', error);
      console.error('📊 [ProductForm] Error.response:', error.response);
      console.error('📋 [ProductForm] Error.response?.data:', error.response?.data);
    } finally {
      setLoadingCategories(false);
      isLoadingFormDataRef.current = false;
      console.log('🏁 [ProductForm] Carga de datos del formulario finalizada');
    }
  };

  const loadSubcategories = async (categoryId) => {
    if (!categoryId) {
      console.log('⏭️ [ProductForm] No hay categoryId, omitiendo carga de subcategorías');
      setSubcategories([]);
      return;
    }
    
    setLoadingSubcategories(true);
    try {
      console.log('🔄 [ProductForm] Cargando subcategorías para categoría ID:', categoryId);
      const response = await classificationService.getSubcategories({
        category_id: categoryId,
        active_only: false
      });
      
      console.log('📥 [ProductForm] Respuesta de subcategorías:');
      console.log('   - Success:', response.success);
      console.log('   - Data type:', typeof response.data);
      console.log('   - Is array:', Array.isArray(response.data));
      console.log('   - Data length:', Array.isArray(response.data) ? response.data.length : 'N/A');
      
      if (response.success) {
        const subcategoriesData = response.data || [];
        console.log('✅ [ProductForm] Subcategorías recibidas:', subcategoriesData.length);
        if (subcategoriesData.length > 0) {
          console.log('📋 [ProductForm] Primeras 3 subcategorías:', subcategoriesData.slice(0, 3).map(s => ({
            id: s.id,
            code: s.code,
            name: s.name,
            category_id: s.category_id,
            is_active: s.is_active
          })));
        }
        setSubcategories(subcategoriesData);
      } else {
        console.warn('⚠️ [ProductForm] Respuesta sin éxito:', response);
        setSubcategories([]);
      }
    } catch (error) {
      console.error('❌ [ProductForm] Error al cargar subcategorías:', error);
      console.error('📊 [ProductForm] Error.response:', error.response);
      console.error('📋 [ProductForm] Error.response?.data:', error.response?.data);
      setSubcategories([]);
    } finally {
      setLoadingSubcategories(false);
      console.log('🏁 [ProductForm] Carga de subcategorías finalizada');
    }
  };

  const loadFamilies = async (subcategoryId) => {
    if (!subcategoryId) {
      console.log('⏭️ [ProductForm] No hay subcategoryId, omitiendo carga de familias');
      setFamilies([]);
      return;
    }
    
    setLoadingFamilies(true);
    try {
      console.log('🔄 [ProductForm] Cargando familias para subcategoría ID:', subcategoryId);
      const response = await classificationService.getFamilies({
        subcategory_id: subcategoryId,
        active_only: false
      });
      
      console.log('📥 [ProductForm] Respuesta de familias:');
      console.log('   - Success:', response.success);
      console.log('   - Data type:', typeof response.data);
      console.log('   - Is array:', Array.isArray(response.data));
      console.log('   - Data length:', Array.isArray(response.data) ? response.data.length : 'N/A');
      
      if (response.success) {
        const familiesData = response.data || [];
        console.log('✅ [ProductForm] Familias recibidas:', familiesData.length);
        if (familiesData.length > 0) {
          console.log('📋 [ProductForm] Primeras 3 familias:', familiesData.slice(0, 3).map(f => ({
            id: f.id,
            code: f.code,
            name: f.name,
            subcategory_id: f.subcategory_id,
            is_active: f.is_active
          })));
        }
        setFamilies(familiesData);
      } else {
        console.warn('⚠️ [ProductForm] Respuesta sin éxito:', response);
        setFamilies([]);
      }
    } catch (error) {
      console.error('❌ [ProductForm] Error al cargar familias:', error);
      console.error('📊 [ProductForm] Error.response:', error.response);
      console.error('📋 [ProductForm] Error.response?.data:', error.response?.data);
      setFamilies([]);
    } finally {
      setLoadingFamilies(false);
      console.log('🏁 [ProductForm] Carga de familias finalizada');
    }
  };

  const generateSkuPreview = () => {
    // Usar la jerarquía completa para obtener los códigos correctos
    if (formData.family_id && formData.category_id && formData.subcategory_id) {
      const category = hierarchy.find(c => c.id === parseInt(formData.category_id));
      if (category) {
        const subcategory = category.subcategories?.find(s => s.id === parseInt(formData.subcategory_id));
        if (subcategory) {
          const family = subcategory.families?.find(f => f.id === parseInt(formData.family_id));
          if (family) {
            const catCode = category.code || 'XXX';
            const subcatCode = subcategory.code || 'XXX';
            const famCode = family.code || 'XXX';
            const sequential = '0001'; // En producción, esto se calcularía
            const sku = `${catCode}-${subcatCode}-${famCode}-${sequential}`;
            console.log('📦 [ProductForm] SKU generado desde generateSkuPreview:', sku);
            setSkuPreview(sku);
            return;
          }
        }
      }
    }
    // Si no se puede generar, limpiar el preview
    setSkuPreview('');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = value;
    
    // Para campos numéricos
    if (['weight', 'length', 'width', 'height', 'min_stock'].includes(name)) {
      processedValue = value.replace(/[^0-9.]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : processedValue,
      // Si se activa has_price_list, activar automáticamente has_pos_prices
      ...(name === 'has_price_list' && checked ? { 
        has_pos_prices: true
      } : {}),
      // Si se desactiva has_price_list, limpiar toda la configuración de precios
      ...(name === 'has_price_list' && !checked ? { 
        has_pos_prices: false, 
        is_taxable: false, 
        pos_prices: {} 
      } : {}),
      // Si se desactiva has_pos_prices, limpiar los precios
      ...(name === 'has_pos_prices' && !checked ? { pos_prices: {} } : {})
    }));
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    console.log('🔄 [ProductForm] Cambio de categoría seleccionada:', categoryId);
    setFormData(prev => ({
      ...prev,
      category_id: categoryId,
      subcategory_id: '', // Resetear subcategoría cuando cambia categoría
      family_id: '', // Resetear familia cuando cambia categoría
    }));
    if (!categoryId) {
      console.log('⏭️ [ProductForm] No se seleccionó categoría');
    }
  };

  const handleSubcategoryChange = (e) => {
    const subcategoryId = e.target.value;
    console.log('🔄 [ProductForm] Cambio de subcategoría seleccionada:', subcategoryId);
    setFormData(prev => ({
      ...prev,
      subcategory_id: subcategoryId,
      family_id: '', // Resetear familia cuando cambia subcategoría
    }));
    if (!subcategoryId) {
      console.log('⏭️ [ProductForm] No se seleccionó subcategoría');
    }
  };

  const validate = () => {
    if (lockNonVendibleFields) {
      setErrors({});
      return true;
    }

    const newErrors = {};

    if (!formData.family_id) {
      newErrors.family_id = 'La familia es obligatoria';
    }

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es obligatorio';
    } else if (formData.name.length > 200) {
      newErrors.name = 'El nombre no puede tener más de 200 caracteres';
    }


    if (formData.min_stock < 0) {
      newErrors.min_stock = 'El stock mínimo no puede ser negativo';
    }

    if (productConversionsEnabled && Array.isArray(formData.conversions) && formData.conversions.length > 0) {
      const seenPairs = new Set();
      formData.conversions.forEach((row, index) => {
        const fromId = Number(row.from_unit_id);
        const toId = Number(row.to_unit_id);
        const rate = parseFloat(row.conversion_rate);
        if (!fromId || !toId) {
          newErrors[`conversions_${index}`] = 'Seleccione la unidad origen';
        } else if (fromId === toId) {
          newErrors[`conversions_${index}`] = 'Las unidades deben ser diferentes';
        } else if (!rate || rate <= 0) {
          newErrors[`conversions_${index}`] = 'La tasa debe ser mayor a 0';
        } else {
          const pairKey = `${fromId}-${toId}`;
          if (seenPairs.has(pairKey)) {
            newErrors[`conversions_${index}`] = 'Conversión duplicada';
          }
          seenPairs.add(pairKey);
        }
      });
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;

    if (!validate()) {
      return;
    }

    setLoading(true);
    console.log('📝 [ProductForm] Enviando formulario...');
    console.log('📦 [ProductForm] Datos:', formData);

    try {
      // Preparar datos para enviar
      let submitData = {
        family_id: formData.family_id,
        name: formData.name,
        unit_of_measure: formData.unit_of_measure,
        min_stock: parseInt(formData.min_stock) || 0,
        is_active: formData.is_active,
        has_price_list: formData.has_price_list,
        is_taxable: formData.has_price_list ? formData.is_taxable : false,
        weight: formData.weight ? parseFloat(formData.weight) : null,
        length: formData.length ? parseFloat(formData.length) : null,
        width: formData.width ? parseFloat(formData.width) : null,
        height: formData.height ? parseFloat(formData.height) : null,
        pos_prices: formData.has_price_list ? Object.keys(formData.pos_prices).reduce((acc, posId) => {
          const price = formData.pos_prices[posId];
          if (price && parseFloat(price) > 0) {
            acc[posId] = parseFloat(price);
          }
          return acc;
        }, {}) : {},
        attributes: Object.keys(formData.attributes || {}).reduce((acc, attrId) => {
          const value = formData.attributes[attrId];
          // Solo incluir atributos con ID válido (mayor a 0) y valor válido
          if (attrId && parseInt(attrId) > 0) {
            // Para valores booleanos, aceptar true/false
            if (typeof value === 'boolean') {
              acc[attrId] = value;
            }
            // Para otros tipos, solo si no está vacío
            else if (value !== null && value !== '' && value !== undefined) {
              acc[attrId] = value;
            }
          }
          return acc;
        }, {}),
        machinery_register_enabled: formData.machinery_register_enabled === true,
        warehouse_ids: warehouseSelectionEnabled
          ? (formData.warehouse_ids || []).map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
          : [],
        machinery_assets: [], // Reservado para cuando se use la asociación vehículo/máquina
      };

      if (productConversionsEnabled) {
        const destinationUnit = units.find((u) => u.code === formData.unit_of_measure);
        const destinationUnitId = destinationUnit?.id ? Number(destinationUnit.id) : null;

        submitData.conversions = (formData.conversions || [])
          .map((row) => ({
            from_unit_id: Number(row.from_unit_id),
            to_unit_id: destinationUnitId || Number(row.to_unit_id),
            conversion_rate: parseFloat(row.conversion_rate),
            description: row.description || null,
            is_active: row.is_active !== false,
          }))
          .filter(
            (row) =>
              Number.isInteger(row.from_unit_id) &&
              row.from_unit_id > 0 &&
              Number.isInteger(row.to_unit_id) &&
              row.to_unit_id > 0 &&
              row.from_unit_id !== row.to_unit_id &&
              !Number.isNaN(row.conversion_rate) &&
              row.conversion_rate > 0
          );
      }

      if (insumoModulesEnabled) {
        submitData.is_insumo = formData.is_insumo === true;
      } else if (!product?.id) {
        submitData.is_insumo = false;
      }

      if (ventasEnabled) {
        submitData.is_vendible = formData.is_vendible === true;
      } else if (!product?.id) {
        submitData.is_vendible = false;
      }

      // Producto de receta: solo se envía vendible (backend ignora el resto).
      if (lockNonVendibleFields) {
        submitData = { is_vendible: formData.is_vendible === true };
      }

      console.log('📤 [ProductForm] Datos a enviar al backend:');
      console.log('   - Attributes:', submitData.attributes);
      console.log('   - Attributes keys:', Object.keys(submitData.attributes || {}));
      console.log('   - Attributes values:', Object.values(submitData.attributes || {}));

      let response;
      if (product && product.id) {
        // Actualizar
        console.log('🔄 [ProductForm] Actualizando producto:', product.id);
        response = await productService.updateProduct(product.id, submitData);
        console.log('✅ [ProductForm] Producto actualizado exitosamente');
        setToast({
          isOpen: true,
          message: 'Producto actualizado exitosamente',
          type: 'success'
        });
      } else {
        // Crear
        console.log('➕ [ProductForm] Creando nuevo producto');
        response = await productService.createProduct(submitData);
        console.log('✅ [ProductForm] Producto creado exitosamente');
        console.log('📦 [ProductForm] Respuesta del servidor:', response);
        setToast({
          isOpen: true,
          message: 'Producto creado exitosamente',
          type: 'success'
        });
      }

      if (response.success) {
        setTimeout(() => {
          onSuccess();
          if (!isPageMode) {
            onClose();
          }
        }, 1000);
      } else {
        throw new Error(response.message || 'Error al guardar el producto');
      }
    } catch (error) {
      console.error('❌ [ProductForm] Error:', error);
      console.error('📊 [ProductForm] Error.response:', error.response);
      console.error('📋 [ProductForm] Error.response?.data:', error.response?.data);
      
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
        setToast({
          isOpen: true,
          message: 'Por favor, corrige los errores en el formulario',
          type: 'error'
        });
      } else {
        const errorMessage = error.response?.data?.message || error.message || 'Error al guardar el producto';
        setErrors({ submit: errorMessage });
        setToast({
          isOpen: true,
          message: errorMessage,
          type: 'error'
        });
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isPageMode) return null;

  // Obtener categoría y subcategoría seleccionadas basadas en la familia
  const selectedFamily = families.find(f => f.id === parseInt(formData.family_id));
  const selectedSubcategory = selectedFamily?.subcategory || 
    (formData.family_id ? subcategories.find(s => s.families?.some(f => f.id === parseInt(formData.family_id))) : null);
  const selectedCategory = selectedSubcategory?.category || 
    (selectedFamily?.category) ||
    (formData.family_id ? categories.find(c => c.subcategories?.some(s => s.families?.some(f => f.id === parseInt(formData.family_id)))) : null);
  
  // Filtrar subcategorías y familias según la selección
  const filteredSubcategories = selectedCategory 
    ? subcategories.filter(s => s.category_id === selectedCategory.id || s.category?.id === selectedCategory.id)
    : subcategories;
    
  const filteredFamilies = selectedSubcategory
    ? families.filter(f => f.subcategory_id === selectedSubcategory.id || f.subcategory?.id === selectedSubcategory.id)
    : families;

  const formContent = (
    <>
      <form onSubmit={handleSubmit} className={isPageMode ? "p-8" : "px-6 py-4"}>
          {errors.submit && (
            <div className="mb-6 bg-red-50 text-red-700 p-4 rounded-lg text-sm border border-red-200 flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {errors.submit}
            </div>
          )}

          <fieldset disabled={readOnly || lockNonVendibleFields} className="border-0 p-0 m-0 min-w-0">
          {lockNonVendibleFields ? (
            <div className="mb-6 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-900">
              Producto terminado de receta: nombre, unidad y datos de ficha se editan en clasificación de recetas.
              Aquí solo puede marcar si es <strong>vendible</strong> para Ventas.
            </div>
          ) : null}
          <div className="space-y-6">
            {/* Sección: SKU Preview - Solo mostrar en modo creación */}
            {!product && skuPreview && (
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 border-2 border-teal-200 rounded-xl p-5 shadow-sm">
                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14" />
                  </svg>
                  SKU (Generado automáticamente)
                </label>
                <div className="text-2xl font-mono font-bold text-teal-700 mb-1">{skuPreview}</div>
                <p className="text-xs text-gray-600 mt-2">Este SKU se generará automáticamente al guardar el producto</p>
              </div>
            )}

            {/* Sección: Información Básica y Clasificación - Misma altura */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Información Básica */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Información Básica
                </h3>
                <div>
                  {/* Nombre */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                      Nombre del Producto <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      maxLength={200}
                      className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                        errors.name ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                      }`}
                      placeholder="Ej: Sofá 3 Plazas Moderno"
                    />
                    {errors.name && (
                      <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                    )}
                    <p className="mt-1 text-xs text-gray-500">Máximo 200 caracteres</p>
                  </div>
                </div>
              </div>

              {/* Clasificación */}
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                  Clasificación <span className="text-red-500">*</span>
                </h3>
                <ClassificationSelector
                  hierarchy={hierarchy}
                  selectedFamilyId={formData.family_id}
                  onSelect={(data) => {
                    console.log('🔄 [ProductForm] Clasificación seleccionada:', data);
                    setFormData(prev => ({
                      ...prev,
                      ...data
                    }));
                    // Generar preview de SKU usando la jerarquía completa
                    if (data.family_id && data.category_id && data.subcategory_id) {
                      // Buscar en la jerarquía los códigos correctos
                      const category = hierarchy.find(c => c.id === parseInt(data.category_id));
                      if (category) {
                        const subcategory = category.subcategories?.find(s => s.id === parseInt(data.subcategory_id));
                        if (subcategory) {
                          const family = subcategory.families?.find(f => f.id === parseInt(data.family_id));
                          if (family) {
                            const catCode = category.code || 'XXX';
                            const subcatCode = subcategory.code || 'XXX';
                            const famCode = family.code || 'XXX';
                            const sequential = '0001'; // En producción, esto se calcularía
                            const sku = `${catCode}-${subcatCode}-${famCode}-${sequential}`;
                            console.log('📦 [ProductForm] SKU generado:', sku);
                            console.log('   - Categoría:', catCode);
                            console.log('   - Subcategoría:', subcatCode);
                            console.log('   - Familia:', famCode);
                            setSkuPreview(sku);
                          }
                        }
                      }
                    }
                  }}
                  disabled={!!product || loadingCategories || readOnly}
                  error={errors.family_id}
                />
                {product && (
                  <p className="mt-2 text-xs text-gray-500">La clasificación no se puede modificar</p>
                )}
              </div>
            </div>

            {/* Sección: Unidad de Medida e Inventario */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
                Unidad de Medida e Inventario
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="unit_of_measure" className="block text-sm font-medium text-gray-700 mb-2">
                    Unidad de Medida <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="unit_of_measure"
                    name="unit_of_measure"
                    value={formData.unit_of_measure}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                  >
                    {units.length > 0 ? (
                      units.map(unit => (
                        <option key={unit.id} value={unit.code}>{unit.symbol} - {unit.name}</option>
                      ))
                    ) : (
                      <option value="">Cargando unidades...</option>
                    )}
                  </select>
                </div>
                <div>
                  <label htmlFor="min_stock" className="block text-sm font-medium text-gray-700 mb-2">
                    Stock Mínimo
                  </label>
                  <input
                    type="text"
                    id="min_stock"
                    name="min_stock"
                    value={formData.min_stock}
                    onChange={handleChange}
                    className={`w-full px-4 py-2.5 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                      errors.min_stock ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 focus:ring-teal-500 focus:border-teal-500'
                    }`}
                    placeholder="0"
                  />
                  {errors.min_stock && (
                    <p className="mt-1 text-sm text-red-600">{errors.min_stock}</p>
                  )}
                </div>
              </div>
            </div>

            {productConversionsEnabled && (() => {
              const destinationUnit = units.find((u) => u.code === formData.unit_of_measure) || null;
              return (
                <ProductConversionsSection
                  conversions={formData.conversions || []}
                  units={units}
                  destinationUnitId={destinationUnit?.id ?? null}
                  destinationUnit={destinationUnit}
                  readOnly={readOnly}
                  onChange={(nextConversions) =>
                    setFormData((prev) => ({ ...prev, conversions: nextConversions }))
                  }
                />
              );
            })()}

            {/* Toggle para activar/desactivar sección de precios */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-sm font-medium text-gray-700">
                    ¿El producto tiene lista de precios?
                  </span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="has_price_list"
                    checked={formData.has_price_list}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-600"></div>
                </label>
              </div>
            </div>

            {/* Sección: Precios - Solo se muestra si está activada */}
            {formData.has_price_list && (
              <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-blue-200">
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Precios por Punto de Venta
                </h3>

                {/* Checkbox para producto gravado */}
                <div className="mb-5">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_taxable"
                      checked={formData.is_taxable}
                      onChange={handleChange}
                      className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
                    />
                    <span className="text-sm font-medium text-gray-700">
                      Producto gravado (con impuestos)
                    </span>
                  </label>
                </div>

                {/* Lista de precios por punto de venta */}
                {pointOfSales.length > 0 ? (
                  <div className="space-y-4">
                    {pointOfSales.map((pos) => (
                      <div key={pos.id} className="bg-white border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-3">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">{pos.name}</h4>
                            {pos.location && (
                              <p className="text-xs text-gray-500 mt-1">{pos.location}</p>
                            )}
                          </div>
                        </div>
                        <div>
                          <label htmlFor={`pos_price_${pos.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                            Precio <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            id={`pos_price_${pos.id}`}
                            value={formData.pos_prices[pos.id] || ''}
                            onChange={(e) => {
                              const value = e.target.value.replace(/[^0-9.]/g, '');
                              setFormData(prev => ({
                                ...prev,
                                pos_prices: {
                                  ...prev.pos_prices,
                                  [pos.id]: value
                                }
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
                    <p className="text-sm">No hay puntos de venta disponibles. Configure puntos de venta en la sección de Configuración.</p>
                  </div>
                )}
              </div>
            )}

            {/* Sección: Atributos del Producto */}
            {attributes.length > 0 && (
              <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-purple-200">
                  <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Atributos del Producto
                </h3>
                <div className="space-y-4">
                  {attributes.map((attribute) => {
                    const attributeValue = formData.attributes[attribute.id] || '';
                    const isRequired = attribute.is_required;
                    const options = attribute.options || [];

                    return (
                      <div key={attribute.id} className="bg-white border border-purple-200 rounded-lg p-4">
                        <label htmlFor={`attribute_${attribute.id}`} className="block text-sm font-medium text-gray-700 mb-2">
                          {attribute.name}
                          {isRequired && <span className="text-red-500 ml-1">*</span>}
                          <span className="ml-2 text-xs text-gray-500">({attribute.type})</span>
                        </label>
                        
                        {attribute.type === 'text' && (
                          <input
                            type="text"
                            id={`attribute_${attribute.id}`}
                            value={attributeValue}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                attributes: {
                                  ...prev.attributes,
                                  [attribute.id]: e.target.value
                                }
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder={`Ingrese ${attribute.name.toLowerCase()}`}
                            required={isRequired}
                          />
                        )}

                        {attribute.type === 'number' && (
                          <input
                            type="number"
                            id={`attribute_${attribute.id}`}
                            value={attributeValue}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                attributes: {
                                  ...prev.attributes,
                                  [attribute.id]: e.target.value
                                }
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            placeholder="0"
                            required={isRequired}
                          />
                        )}

                        {attribute.type === 'select' && (
                          <select
                            id={`attribute_${attribute.id}`}
                            value={attributeValue}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                attributes: {
                                  ...prev.attributes,
                                  [attribute.id]: e.target.value
                                }
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            required={isRequired}
                          >
                            <option value="">Seleccione una opción</option>
                            {options.map((option, index) => (
                              <option key={index} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        )}

                        {attribute.type === 'boolean' && (
                          <label className="flex items-center gap-3 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={attributeValue === 'true' || attributeValue === true}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  attributes: {
                                    ...prev.attributes,
                                    [attribute.id]: e.target.checked
                                  }
                                }));
                              }}
                              className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
                            />
                            <span className="text-sm text-gray-600">
                              {attributeValue === 'true' || attributeValue === true ? 'Sí' : 'No'}
                            </span>
                          </label>
                        )}

                        {attribute.type === 'date' && (
                          <input
                            type="date"
                            id={`attribute_${attribute.id}`}
                            value={attributeValue}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                attributes: {
                                  ...prev.attributes,
                                  [attribute.id]: e.target.value
                                }
                              }));
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all"
                            required={isRequired}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Sección: Opciones */}
            <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800 mb-5 flex items-center gap-2 pb-3 border-b border-gray-200">
                <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                Opciones del Producto
              </h3>
              <label className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={formData.is_active}
                  onChange={handleChange}
                  className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                />
                <span className="ml-3 text-sm font-medium text-gray-700">Producto activo</span>
              </label>

              {insumoModulesEnabled && (
                <label className="flex items-start gap-3 p-3 mt-2 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer border-t border-gray-100 pt-4">
                  <input
                    type="checkbox"
                    name="is_insumo"
                    checked={formData.is_insumo === true}
                    onChange={handleChange}
                    className="w-5 h-5 mt-0.5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Tratar este producto como insumo</span>
                    <span className="block text-xs text-gray-500 mt-1">
                      Ingredientes en recetas (Producción) y materiales en salidas a producción (concesionaria).
                    </span>
                  </span>
                </label>
              )}

              {warehouseSelectionEnabled && (
                <div className="mt-4 border-t border-gray-100 pt-4">
                  <div className="mb-2 text-sm font-medium text-gray-700">Visible en almacenes</div>
                  <p className="text-xs text-gray-500 mb-3">
                    Si no selecciona ningún almacén, el producto se mostrará en todos.
                  </p>
                  {warehouses.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {warehouses.map((warehouse) => {
                        const wid = Number(warehouse.id);
                        const checked = (formData.warehouse_ids || []).includes(wid);
                        return (
                          <label
                            key={warehouse.id}
                            className="flex items-center gap-2 text-sm text-gray-700 border border-gray-200 rounded-md px-3 py-2 hover:bg-gray-50 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) => {
                                setFormData((prev) => {
                                  const current = new Set((prev.warehouse_ids || []).map((id) => Number(id)));
                                  if (e.target.checked) current.add(wid);
                                  else current.delete(wid);
                                  return { ...prev, warehouse_ids: Array.from(current) };
                                });
                              }}
                              className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span>{warehouse.name}</span>
                            <span className="text-xs text-gray-400">{warehouse.code ? `(${warehouse.code})` : ''}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500">No hay almacenes disponibles.</div>
                  )}
                </div>
              )}
            </div>

            {/* Módulo Maquinaria: solo indicar si el producto tiene habilitado el registro de lectura */}
            {machineryEnabled && (
              <div className="bg-white border border-gray-200 rounded-xl p-6 shadow-sm">
                <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center gap-2 pb-3 border-b border-gray-200">
                  <span className="text-xl" aria-hidden>🚜</span>
                  Registrar lectura de vehículo/máquina
                </h3>
                <label className="flex items-center p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    name="machinery_register_enabled"
                    checked={formData.machinery_register_enabled === true}
                    onChange={(e) => setFormData((prev) => ({ ...prev, machinery_register_enabled: e.target.checked }))}
                    className="w-5 h-5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">Este producto tiene habilitada la opción de registrar lectura de vehículo/máquina</span>
                </label>
              </div>
            )}
          </div>
          </fieldset>

          {ventasEnabled && (
            <div className={`mt-6 bg-white border rounded-xl p-6 shadow-sm ${lockNonVendibleFields ? 'border-teal-300 ring-1 ring-teal-100' : 'border-gray-200'}`}>
              <h3 className="text-lg font-semibold text-gray-800 mb-2 pb-3 border-b border-gray-200">
                Ventas
              </h3>
              <fieldset disabled={readOnly} className="border-0 p-0 m-0 min-w-0">
                <label className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_vendible"
                    checked={formData.is_vendible === true}
                    onChange={handleChange}
                    className="w-5 h-5 mt-0.5 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <span>
                    <span className="block text-sm font-medium text-gray-800">Producto vendible</span>
                    <span className="block text-xs text-gray-500 mt-1">
                      {lockNonVendibleFields
                        ? 'Incluir este producto terminado de receta en la lista de precios de Ventas.'
                        : 'Incluir en la lista de precios del módulo Ventas (insumos u otros productos fuera del catálogo habitual).'}
                    </span>
                  </span>
                </label>
              </fieldset>
            </div>
          )}

          {/* Buttons */}
          <div className="mt-8 flex justify-end gap-4 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-all duration-200 font-medium shadow-sm hover:shadow"
              disabled={loading}
            >
              {readOnly ? 'Volver' : 'Cancelar'}
            </button>
            {!readOnly && (
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-700 hover:to-teal-800 text-white rounded-lg transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center gap-2"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Guardando...
                  </>
                ) : (
                  <>
                    {product ? 'Actualizar Producto' : 'Crear Producto'}
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </>
                )}
              </button>
            )}
          </div>
        </form>
        
        {/* Toast Notification */}
        {toast.isOpen && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast({ ...toast, isOpen: false })}
          />
        )}
      </>
  );

  if (isPageMode) {
    return (
      <div className="bg-white rounded-lg shadow-md">
        {formContent}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 my-8">
        {/* Header del modal */}
        <div className="px-6 py-4 border-b border-gray-200 sticky top-0 bg-white z-10">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-semibold text-gray-800">
              {product ? (readOnly ? 'Ver Producto' : 'Editar Producto') : 'Nuevo Producto'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        {formContent}
      </div>
    </div>
  );
};

export default ProductForm;

